<script setup lang="ts">
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";
import { useProject } from "@/stores/project/project.store";

import BIMFormat from "@/services/Utils/BIMFormat";

const projectStore = useProject();
const { lifetimeTable } = storeToRefs(projectStore);

const props = defineProps<{
  material: any;
  canEdit: boolean;
}>();

const emits = defineEmits<{
  (event: "toggle-lifetime-table"): void;
  (event: "setLifetime", payload: { name: string; value: number }): void;
  (event: "updateLifetime", payload: number): void;
}>();

const BIMTypeCode = computed(() => {
  return props.material.additionalParameters?.BIMTypeCode;
});

const suggestions = computed<{ name: string; value: number }[]>(() => {
  const row = lifetimeTable.value.rows.find((row: any) => BIMFormat(row.constructionId) === BIMTypeCode.value);

  if (!row || !row.materialLifeTime) return [];

  const materialLifeTime = row.materialLifeTime as Record<string, number>;

  return Object.entries(materialLifeTime).map(([name, value]) => ({
    name,
    value,
  }));
});

const selectedNamed = ref<string | null>(null);

const selectSuggestion = (value: number, name: string) => {
  selectedNamed.value = name;
  emits("updateLifetime", Number(value));
};
</script>

<template>
  <div class="rounded-4 flex flex-col gap-2 bg-slate-100 p-3">
    <div class="flex items-end gap-3">
      <div
        v-if="suggestions"
        class="rounded-4 flex h-full flex-1 flex-col bg-slate-200 py-2 text-sm">
        <span
          v-if="BIMTypeCode"
          class="ml-2 pb-1 text-xs font-medium text-slate-500">
          Classification Code: {{ BIMTypeCode }}</span
        >
        <span
          v-else
          class="flex h-full items-center text-center align-middle text-xs font-medium text-slate-500">
          To use suggestions, <br />
          please add a Classification Code to your material.</span
        >
        <div class="flex flex-col text-sm">
          <div
            v-for="suggestion in suggestions"
            :key="suggestion.name"
            class="flex cursor-pointer items-center justify-between gap-x-3 px-4 py-1 transition-all select-none hover:bg-slate-300"
            :class="[selectedNamed === suggestion.name ? 'bg-slate-300 font-semibold' : 'hover:bg-slate-100']"
            @click="selectSuggestion(suggestion.value, suggestion.name)">
            <span class="whitespace-nowrap">{{ suggestion.name }}</span>
            <span class="whitespace-nowrap">{{ suggestion.value }} years</span>
          </div>
        </div>
      </div>

      <slot />
    </div>
  </div>
</template>
